import React, { useState, useEffect, useCallback, useRef } from "react";
import "./FeatureGallery.css";

// Galería de la home (opción E de /galerias, "Destacada + cuatro"): bloques con
// una foto grande y cuatro pequeñas al lado; la grande alterna de lado. Al
// tocar una foto se abre a pantalla completa. Los vídeos del panel se añaden
// al final como casillas más, en bucle y sin sonido.
//
// Los archivos son los del panel (Imágenes del mosaico): no cambiar los nombres.
const PHOTOS = [
  { src: "img/1.webp", w: 960, h: 1280, alt: "Sushi y nigiri de pescado fresco en Sun Taka, Barcelona" },
  { src: "img/2.webp", w: 720, h: 1280, alt: "Sashimi de pescado de temporada — Sun Taka, izakaya en Barcelona" },
  { src: "img/3.webp", w: 960, h: 1280, alt: "Plato de la carta japonesa de Sun Taka, Eixample (Barcelona)" },
  { src: "img/4.webp", w: 853, h: 1280, alt: "Especialidad de wagyu A5 en Sun Taka, restaurante japonés en Barcelona" },
  { src: "img/5.webp", w: 1280, h: 853, alt: "Cocina japonesa de autor del chef Taka en Sun Taka, Barcelona" },
  { src: "img/5.2.webp", w: 826, h: 1100, alt: "Marisco fresco preparado al estilo japonés en Sun Taka, Barcelona" },
  { src: "img/6.1.webp", w: 960, h: 1280, alt: "Plato de sushi de Sun Taka, restaurante izakaya en Barcelona" },
  { src: "img/7.1.webp", w: 626, h: 1100, alt: "Tataki y brasa japonesa en Sun Taka, Barcelona" },
  { src: "img/8.webp", w: 720, h: 1280, alt: "Detalle de un plato japonés de la carta de Sun Taka, Barcelona" },
  { src: "img/9.webp", w: 563, h: 1000, alt: "Ambiente de la izakaya Sun Taka en el Eixample, Barcelona" },
  { src: "img/10.webp", w: 1131, h: 1280, alt: "Sushi y nigiri de pescado fresco en Sun Taka, Barcelona" },
  { src: "img/10.1.webp", w: 821, h: 1100, alt: "Sashimi de pescado de temporada — Sun Taka, izakaya en Barcelona" },
  { src: "img/10.2.webp", w: 948, h: 1200, alt: "Plato de la carta japonesa de Sun Taka, Eixample (Barcelona)" },
  { src: "img/11.1.webp", w: 619, h: 1100, alt: "Especialidad de wagyu A5 en Sun Taka, restaurante japonés en Barcelona" },
  { src: "img/12.webp", w: 912, h: 1280, alt: "Cocina japonesa de autor del chef Taka en Sun Taka, Barcelona" },
  { src: "img/13.webp", w: 563, h: 1000, alt: "Marisco fresco preparado al estilo japonés en Sun Taka, Barcelona" },
  { src: "img/14.webp", w: 667, h: 1000, alt: "Plato de sushi de Sun Taka, restaurante izakaya en Barcelona" },
  { src: "img/15.webp", w: 960, h: 1280, alt: "Tataki y brasa japonesa en Sun Taka, Barcelona" },
  { src: "img/16.webp", w: 1280, h: 960, alt: "Detalle de un plato japonés de la carta de Sun Taka, Barcelona" },
];

// Tamaño de cada casilla según cuántas tiene el bloque, para que el último
// bloque (si no llega a 5) quede completo y sin huecos.
const SIZES = {
  1: ["full"],
  2: ["big", "big"],
  3: ["big", "wide", "wide"],
  4: ["big", "", "", "wide"],
  5: ["big", "", "", "", ""],
};

function Chevron({ dir }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        d={dir === "left" ? "M15 4 7 12l8 8" : "M9 4l8 8-8 8"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Lightbox({ index, onClose, onMove }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onMove(-1);
      else if (e.key === "ArrowRight") onMove(1);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onMove]);

  // Deslizar con el dedo para cambiar de foto en móvil.
  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) onMove(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const p = PHOTOS[index];
  return (
    <div className="fg-lightbox" role="dialog" aria-modal="true" onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <img key={p.src} src={p.src} alt={p.alt} onClick={(e) => e.stopPropagation()} />
      <button type="button" className="fg-lightbox-close" onClick={onClose} aria-label="Cerrar">×</button>
      <button type="button" className="fg-arrow fg-prev" onClick={(e) => { e.stopPropagation(); onMove(-1); }} aria-label="Anterior"><Chevron dir="left" /></button>
      <button type="button" className="fg-arrow fg-next" onClick={(e) => { e.stopPropagation(); onMove(1); }} aria-label="Siguiente"><Chevron dir="right" /></button>
      <span className="fg-counter">{index + 1} / {PHOTOS.length}</span>
    </div>
  );
}

export default function FeatureGallery({ videos = [], className = "" }) {
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const move = useCallback((d) => setOpen((i) => (i + d + PHOTOS.length) % PHOTOS.length), []);

  const items = [
    ...PHOTOS.map((photo, index) => ({ key: photo.src, photo, index })),
    ...videos.map((file) => ({ key: "v-" + file, video: file })),
  ];
  const blocks = [];
  for (let i = 0; i < items.length; i += 5) blocks.push(items.slice(i, i + 5));

  return (
    <div className={`fg ${className}`}>
      {blocks.map((block, b) => (
        <div key={block[0].key} className={`fg-block ${b % 2 ? "reverse" : ""}`}>
          {block.map((it, j) => {
            const size = SIZES[block.length][j];
            if (it.video) {
              return (
                <div key={it.key} className={`fg-item fg-video ${size}`}>
                  <video
                    src={`/img/${it.video}`}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label="Vídeo del restaurante Sun Taka, Barcelona"
                  />
                </div>
              );
            }
            const p = it.photo;
            return (
              <button key={it.key} type="button" className={`fg-item ${size}`} onClick={() => setOpen(it.index)} aria-label={`Ampliar: ${p.alt}`}>
                <img loading="lazy" decoding="async" src={p.src} width={p.w} height={p.h} alt={p.alt} />
              </button>
            );
          })}
        </div>
      ))}

      {open !== null && <Lightbox index={open} onClose={close} onMove={move} />}
    </div>
  );
}
